import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Trophy, Plus, Trash2, RefreshCw, Search, Clock, CheckCircle2, XCircle,
  AlertTriangle, Loader2, TrendingDown,
} from "lucide-react";
import { apiGet, apiFetch, safeJson } from "@/lib/api";
import { LoadingBlock } from "@/components/Spinner";

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso.replace("Z", "+00:00")).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const STATUS = {
  waiting: { label: "Waiting", color: "#94a3b8", Icon: Clock },
  bet_placed: { label: "Bet placed", color: "#3b82f6", Icon: CheckCircle2 },
  set1_lost_dca: { label: "Set 1 lost — DCA", color: "#f59e0b", Icon: TrendingDown },
  set2_lost_dca: { label: "Set 2 lost — DCA", color: "#f97316", Icon: TrendingDown },
  won: { label: "Won", color: "#10b981", Icon: Trophy },
  lost: { label: "Lost", color: "#ef4444", Icon: XCircle },
  missed: { label: "Missed window", color: "#6b7280", Icon: AlertTriangle },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.waiting;
  const Icon = s.Icon;
  return (
    <span data-testid="mw-status-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
      style={{ backgroundColor: s.color + "22", color: s.color, border: `1px solid ${s.color}55` }}>
      <Icon size={11} /> {s.label}
    </span>
  );
};

export default function MatchWinner() {
  const [selections, setSelections] = useState([]);
  const [prematch, setPrematch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPre, setLoadingPre] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [busyEvent, setBusyEvent] = useState("");
  const [liveOdds, setLiveOdds] = useState({});       // {event_id: {home_odds, away_odds}}
  const [oddsLoading, setOddsLoading] = useState({}); // {event_id: true}
  const [selOdds, setSelOdds] = useState({});         // live odds for active selection cards

  const loadSelections = useCallback(async () => {
    try {
      const d = await apiGet("/api/match-winner/selections");
      const sels = d?.selections || [];
      setSelections(sels);
      // Refresh the CURRENT live moneyline price on active cards (auto-refreshes every 20s with this poll).
      const activeIds = sels
        .filter((s) => ["waiting", "bet_placed", "set1_lost_dca", "set2_lost_dca"].includes(s.status))
        .map((s) => s.event_id);
      if (activeIds.length) {
        try {
          const r = await apiFetch("/api/match-winner/odds", { method: "POST", body: JSON.stringify({ event_ids: activeIds }) });
          const od = await safeJson(r);
          if (od?.odds) setSelOdds(od.odds);
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const loadPrematch = useCallback(async () => {
    setLoadingPre(true);
    try {
      const d = await apiGet("/api/match-winner/prematch");
      setPrematch(d?.matches || []);
    } catch { toast.error("Could not load match list"); }
    setLoadingPre(false);
  }, []);

  useEffect(() => {
    loadSelections();
    const t = setInterval(loadSelections, 20000);
    return () => clearInterval(t);
  }, [loadSelections]);

  const openAdd = () => { setShowAdd(true); loadPrematch(); };

  const addSelection = async (event_id, our_side, player) => {
    setBusyEvent(event_id + our_side);
    try {
      const res = await apiFetch("/api/match-winner/selections", {
        method: "POST", body: JSON.stringify({ event_id, our_side }),
      });
      const d = await safeJson(res);
      if (res.ok) {
        toast.success(`Added ${player} to Match Winners`);
        await loadSelections();
      } else {
        toast.error(d?.detail || "Could not add match");
      }
    } catch { toast.error("Could not add match"); }
    setBusyEvent("");
  };

  const removeSelection = async (id) => {
    try {
      const res = await apiFetch(`/api/match-winner/selections/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Removed"); setSelections((s) => s.filter((x) => x.id !== id)); }
      else toast.error("Could not remove");
    } catch { toast.error("Could not remove"); }
  };

  const selectedEventIds = useMemo(() => new Set(selections.map((s) => s.event_id)), [selections]);

  const filteredPre = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return prematch;
    return prematch.filter((m) => (m.name || "").toLowerCase().includes(q));
  }, [prematch, search]);

  // Lazily fetch the REAL live moneyline price for displayed matches that don't already have a feed
  // price. Probing is per-selection, so we only load the first ~15 currently-shown matches (and more
  // as the search narrows), cached server-side. This shows the exact price a bet will be placed at.
  useEffect(() => {
    if (!showAdd) return;
    const need = filteredPre
      .filter((m) => !m.home_odds && !m.away_odds && !liveOdds[m.event_id] && !oddsLoading[m.event_id])
      .slice(0, 15)
      .map((m) => m.event_id);
    if (need.length === 0) return;
    setOddsLoading((o) => { const n = { ...o }; need.forEach((id) => (n[id] = true)); return n; });
    (async () => {
      try {
        const res = await apiFetch("/api/match-winner/odds", { method: "POST", body: JSON.stringify({ event_ids: need }) });
        const d = await safeJson(res);
        if (d?.odds) setLiveOdds((prev) => ({ ...prev, ...d.odds }));
      } catch { /* ignore */ }
      setOddsLoading((o) => { const n = { ...o }; need.forEach((id) => delete n[id]); return n; });
    })();
  }, [showAdd, filteredPre, liveOdds, oddsLoading]);

  // While the picker is open, keep the shown prices CURRENT: every 20s force-refresh the moneyline
  // odds for the matches we've already loaded (so the price you see before committing is up to date).
  useEffect(() => {
    if (!showAdd) return;
    const t = setInterval(async () => {
      const ids = filteredPre
        .filter((m) => !m.home_odds && !m.away_odds && liveOdds[m.event_id])
        .slice(0, 15)
        .map((m) => m.event_id);
      if (ids.length === 0) return;
      try {
        const res = await apiFetch("/api/match-winner/odds", { method: "POST", body: JSON.stringify({ event_ids: ids, force: true }) });
        const d = await safeJson(res);
        if (d?.odds) setLiveOdds((prev) => ({ ...prev, ...d.odds }));
      } catch { /* ignore */ }
    }, 20000);
    return () => clearInterval(t);
  }, [showAdd, filteredPre, liveOdds]);

  const active = selections.filter((s) => ["waiting", "bet_placed", "set1_lost_dca", "set2_lost_dca"].includes(s.status));
  const done = selections.filter((s) => ["won", "lost", "missed"].includes(s.status));

  return (
    <div className="max-w-5xl mx-auto pb-24" data-testid="match-winner-screen">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy size={22} className="text-[#f59e0b]" />
          <div>
            <h1 className="text-lg font-bold text-white">Match Winners</h1>
            <p className="text-[12px] text-[#94a3b8]">Pick the match winner. Backs them on the Match-Winner market at live odds.</p>
          </div>
        </div>
        <button data-testid="mw-add-btn" onClick={openAdd}
          className="flex items-center gap-1.5 bg-[#f59e0b] hover:bg-[#d97706] text-white font-bold text-sm px-3.5 py-2 rounded-xl">
          <Plus size={16} /> Add Match
        </button>
      </div>

      <div className="bg-[#1e293b]/60 border border-[#334155] rounded-xl p-3 mb-4 text-[12px] text-[#cbd5e1] leading-relaxed">
        Enable <span className="text-[#fbbf24] font-semibold">Match Winner Strategy</span> in Settings, then add matches here.
        The engine places the bet pre-match (or until 4 games of set 1 are done). If DCA is enabled, it
        places one extra bet on the same player at the new odds — either when your player loses set 1,
        or when your player wins set 1 then loses set 2 (before set 3). Works independently of your other strategies.
      </div>

      {loading ? (
        <LoadingBlock />
      ) : (
        <>
          <h2 className="text-base font-bold text-[#e2e8f0] mb-2">Active ({active.length})</h2>
          {active.length === 0 ? (
            <div className="flex flex-col items-center py-10 bg-[#1e293b]/40 rounded-2xl mb-5">
              <Trophy size={44} color="#475569" />
              <p className="text-[#6b7280] mt-3">No matches selected yet</p>
              <button onClick={openAdd} className="text-[#f59e0b] text-sm font-semibold mt-1">Add your first match</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {active.map((s) => (
                <SelectionCard key={s.id} sel={s} onRemove={() => removeSelection(s.id)} liveOdds={selOdds[s.event_id]} />
              ))}
            </div>
          )}

          {done.length > 0 && (
            <>
              <h2 className="text-base font-bold text-[#e2e8f0] mb-2 mt-2">Completed ({done.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {done.map((s) => (
                  <SelectionCard key={s.id} sel={s} onRemove={() => removeSelection(s.id)} dimmed />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 p-0 md:p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-[#0f172a] border border-[#334155] rounded-t-2xl md:rounded-2xl w-full md:max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="mw-add-modal">
            <div className="p-4 border-b border-[#334155] flex items-center justify-between">
              <h3 className="text-base font-bold text-white">Add a match (live & upcoming)</h3>
              <button onClick={loadPrematch} className="text-[#94a3b8] hover:text-white"><RefreshCw size={18} className={loadingPre ? "animate-spin" : ""} /></button>
            </div>
            <div className="p-3 border-b border-[#334155]">
              <div className="flex items-center gap-2 bg-[#1e293b] rounded-lg px-3 py-2">
                <Search size={16} className="text-[#64748b]" />
                <input data-testid="mw-search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search players…" className="bg-transparent text-sm text-white outline-none flex-1" />
              </div>
              <p className="text-[11px] text-[#64748b] mt-2">
                Tap the player you expect to WIN. Each button shows their live match-winner price.
              </p>
            </div>
            <div className="overflow-y-auto p-3 space-y-2">
              {loadingPre ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#f59e0b]" /></div>
              ) : filteredPre.length === 0 ? (
                <p className="text-center text-[#6b7280] py-8 text-sm">No matches found right now.</p>
              ) : (
                filteredPre.map((m) => {
                  const added = selectedEventIds.has(m.event_id);
                  return (
                    <div key={m.event_id} className="bg-[#1e293b] rounded-xl p-3" data-testid="mw-prematch-row">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-[#64748b]">{m.competition} · {m.is_live ? (m.score || "Live") : fmtTime(m.start_time)}</span>
                        <span className="flex items-center gap-1.5">
                          {m.is_live && <span className="text-[10px] text-[#ef4444] font-bold flex items-center gap-1" data-testid="mw-live-badge"><span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse" />LIVE</span>}
                          {added && <span className="text-[10px] text-[#10b981] font-bold">ADDED</span>}
                        </span>
                      </div>
                      <p className="text-sm text-white font-semibold mb-2">{m.name}</p>
                      <div className="flex gap-2">
                        {[["home", m.home_player], ["away", m.away_player]].map(([side, player]) => {
                          const feedOdds = side === "home" ? m.home_odds : m.away_odds;
                          const probed = liveOdds[m.event_id] ? (side === "home" ? liveOdds[m.event_id].home_odds : liveOdds[m.event_id].away_odds) : null;
                          const odds = feedOdds || probed;
                          const isLoadingOdds = !odds && oddsLoading[m.event_id];
                          return (
                          <button key={side} disabled={added || busyEvent === m.event_id + side}
                            data-testid={`mw-pick-${side}`}
                            onClick={() => addSelection(m.event_id, side, player)}
                            className="flex-1 flex flex-col items-center bg-[#0f172a] hover:bg-[#f59e0b]/20 border border-[#334155] hover:border-[#f59e0b] disabled:opacity-40 disabled:hover:bg-[#0f172a] disabled:hover:border-[#334155] rounded-lg px-2 py-2 transition-colors">
                            <span className="text-[13px] text-[#e2e8f0] font-semibold truncate w-full text-center">{player}</span>
                            {busyEvent === m.event_id + side ? (
                              <span className="text-[11px] text-[#f59e0b] font-bold mt-0.5">Adding…</span>
                            ) : (
                              <span className="text-[11px] font-bold mt-0.5" data-testid={`mw-odds-${side}`}>
                                {odds
                                  ? <span className="text-[#f59e0b]">to win @ {Number(odds).toFixed(2)}x</span>
                                  : isLoadingOdds
                                    ? <span className="text-[#64748b] font-medium inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> loading odds…</span>
                                    : <span className="text-[#475569] font-medium">· odds unavailable</span>}
                              </span>
                            )}
                          </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="p-3 border-t border-[#334155]">
              <button onClick={() => setShowAdd(false)} className="w-full bg-[#334155] hover:bg-[#475569] text-white font-semibold py-2.5 rounded-xl">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SelectionCard = ({ sel, onRemove, dimmed, liveOdds }) => {
  const nowPrice = liveOdds ? (sel.our_side === "home" ? liveOdds.home_odds : liveOdds.away_odds) : null;
  const oppNow = liveOdds ? (sel.our_side === "home" ? liveOdds.away_odds : liveOdds.home_odds) : null;
  // Live cash-out profit %: hedge the opposite side at current opponent odds.
  // net_profit = payout_if_win - total_staked - hedge_stake ; hedge_stake = payout_if_win / oppNow.
  let profitPct = null;
  const legs = (sel.bets || []).filter((b) => Number(b.odds) > 1 && Number(b.stake) > 0);
  if (oppNow > 1 && legs.length) {
    const staked = legs.reduce((t, b) => t + Number(b.stake), 0);
    const payout = legs.reduce((t, b) => t + Number(b.stake) * Number(b.odds), 0);
    if (staked > 0) {
      const hedge = payout / oppNow;
      profitPct = ((payout - staked - hedge) / staked) * 100;
    }
  }
  const profitColor = profitPct == null ? "#94a3b8" : profitPct >= 0 ? "#10b981" : "#ef4444";
  const showProfit = ["bet_placed", "set1_lost_dca", "set2_lost_dca"].includes(sel.status);
  return (
  <div className={`bg-[#1e293b] rounded-2xl p-4 ${dimmed ? "opacity-70" : ""}`} style={{ borderLeft: `3px solid ${(STATUS[sel.status] || STATUS.waiting).color}` }} data-testid="mw-selection-card">
    <div className="flex items-start justify-between mb-2">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-[#64748b]">{sel.event_name}</p>
        <p className="text-[15px] font-bold text-white truncate" data-testid="mw-backed-player">
          Backing: <span className="text-[#fbbf24]">{sel.our_player}</span>
        </p>
        <p className="text-[12px] text-[#94a3b8]">To win the match · Best of {sel.best_of} · vs {sel.opponent}</p>
      </div>
      <button data-testid="mw-remove-btn" onClick={onRemove} className="text-[#64748b] hover:text-[#ef4444] p-1.5"><Trash2 size={16} /></button>
    </div>
    <div className="flex items-center justify-between">
      <StatusBadge status={sel.status} />
      <span className="text-[11px] text-[#475569]">
        {sel.bet_count > 0 ? `${sel.bet_count} bet${sel.bet_count > 1 ? "s" : ""} placed` : (sel.start_time ? fmtTime(sel.start_time) : "")}
      </span>
    </div>
    {!dimmed && (
      <div className="flex items-center gap-3 mt-2 text-[11px]" data-testid="mw-card-odds">
        {sel.last_odds > 1 && <span className="text-[#94a3b8]">Placed @ <span className="text-[#e2e8f0] font-semibold">{Number(sel.last_odds).toFixed(2)}x</span></span>}
        {nowPrice > 1 && (
          <span className="text-[#94a3b8] inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] animate-pulse" /> now @ <span className="text-[#10b981] font-semibold">{Number(nowPrice).toFixed(2)}x</span>
          </span>
        )}
        {showProfit && (
          <span className="ml-auto inline-flex items-center gap-1" data-testid="mw-cashout-profit">
            <span className="text-[#64748b]">cash-out</span>
            <span className="font-bold" style={{ color: profitColor }}>
              {profitPct == null ? "—" : `${profitPct >= 0 ? "+" : ""}${profitPct.toFixed(1)}%`}
            </span>
          </span>
        )}
      </div>
    )}
    {sel.status_reason && <p className="text-[11px] text-[#64748b] mt-2">{sel.status_reason}</p>}
  </div>
  );
};
